import { UserPlusIcon } from 'lucide-react';
import { Formik } from 'formik';

import {
  Environment,
  CreateAccessValues,
} from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { FeatureId } from '@/react/portainer/feature-flags/enums';

import { Widget, WidgetBody, WidgetTitle } from '@@/Widget';
import { TextTip } from '@@/Tip/TextTip';
import { Alert } from '@@/Alert';

import { useAccesses } from '../queries/useAccesses';
import { useUpdateEnvironmentAccess } from '../queries/useUpdateEnvironmentAccess';
import { PolicyType } from '../../policies/types';

import { CreateAccessInnerForm } from './CreateAccessInnerForm';

interface Props {
  environment?: Environment;
  group?: EnvironmentGroup;
  minimumAgentVersions?: Record<PolicyType, string>;
  isLoading: boolean;
  onSuccess(): void;
}

export function CreateAccessWidget({
  environment,
  group,
  minimumAgentVersions,
  isLoading,
  onSuccess,
}: Props) {
  const {
    roles,
    hasPolicyOverride,
    availableUsersAndTeams,
    isLoading: isAccessesLoading,
    error: accessError,
  } = useAccesses(environment, group, minimumAgentVersions);
  const updateMutation = useUpdateEnvironmentAccess();

  const initialValues: CreateAccessValues = {
    selectedUsersAndTeams: [],
    selectedRoleId: roles?.[0]?.Id,
  };

  if (hasPolicyOverride) {
    return null;
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget aria-label="Create access">
          <WidgetTitle icon={UserPlusIcon} title="Create access" />
          <WidgetBody>
            {accessError ? (
              <Alert color="error" title="Error loading access">
                {accessError.message ?? 'Unknown error'}
              </Alert>
            ) : (
              <>
                <TextTip
                  className="mb-2"
                  childrenWrapperClassName="text-warning"
                >
                  Adding user access will require the affected user(s) to logout
                  and login for the changes to be taken into account.
                </TextTip>

                <Formik<CreateAccessValues>
                  initialValues={initialValues}
                  enableReinitialize
                  onSubmit={handleSubmit}
                  validateOnMount
                >
                  {(formikProps) => (
                    <CreateAccessInnerForm
                      values={formikProps.values}
                      setFieldValue={formikProps.setFieldValue}
                      isValid={formikProps.isValid}
                      dirty={formikProps.dirty}
                      availableUsersAndTeams={availableUsersAndTeams}
                      roles={roles}
                      isLoading={isLoading || isAccessesLoading}
                      isUpdating={updateMutation.isLoading}
                      limitedFeature={FeatureId.RBAC_ROLES}
                    />
                  )}
                </Formik>
              </>
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );

  async function handleSubmit(
    values: CreateAccessValues,
    { resetForm }: { resetForm: () => void }
  ) {
    if (!environment) {
      return;
    }
    try {
      const newUserPolicies = { ...environment.UserAccessPolicies };
      const newTeamPolicies = { ...environment.TeamAccessPolicies };

      values.selectedUsersAndTeams.forEach((access) => {
        if (access.Type === 'user') {
          newUserPolicies[access.Id] = { RoleId: values.selectedRoleId! };
        } else {
          newTeamPolicies[access.Id] = { RoleId: values.selectedRoleId! };
        }
      });

      await updateMutation.mutateAsync({
        id: environment.Id,
        userAccessPolicies: newUserPolicies,
        teamAccessPolicies: newTeamPolicies,
      });

      notifySuccess('Success', 'Access successfully updated');
      resetForm();
      onSuccess();
    } catch (error) {
      notifyError('Failed to update access', error as Error);
    }
  }
}
